/**
 * BoosterHome - Campaign Pack with Facebook Preview + Crop + Publish (P3)
 */
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import ImageCropModal from '@/components/ImageCropModal';
import AdGraphicEditor from '@/components/AdGraphicEditor';
import VideoEditor from '@/components/VideoEditor';
import ContextPanel from '@/components/ContextPanel';
import CardPremium from '@/components/CardPremium';
import TemplateLibrary from '@/components/TemplateLibrary';
import PhotoUploader from '@/components/PhotoUploader';
import MediaPicker from '@/components/MediaPicker';
import { API } from '@/utils/api';
import {
  Rocket, Copy, RefreshCw, Loader2, CheckCircle2,
  Facebook, Instagram, Target, Flame, Sparkles, ArrowRight,
  Download, ImageIcon, Send, LinkIcon, X, Crop,
  ThumbsUp, MessageCircle, Share2, Globe, Clock, Calendar, Trash2, Paintbrush, Film,
  Inbox, Upload, Check, Plus
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CARD_META = {
  facebook_post: { icon: Facebook, color: '#1877F2', bg: '#EBF5FF', label: 'Facebook', desc: 'Post gotowy do publikacji', platform: 'facebook' },
  instagram_caption: { icon: Instagram, color: '#E1306C', bg: '#FDF2F8', label: 'Instagram', desc: 'Caption z hashtagami', platform: 'instagram' },
  cta: { icon: Target, color: '#F59E0B', bg: '#FFFBEB', label: 'Call to Action', desc: 'Zachęta do kontaktu', platform: 'other' },
  promo_idea: { icon: Flame, color: '#10B981', bg: '#ECFDF5', label: 'Pomysł promocji', desc: 'Gotowy do wdrożenia', platform: 'other' },
};

/* ==================== SUB-COMPONENTS ==================== */

function ScheduleModal({ card, packId, onClose, onScheduled }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('18:00');
  const [saving, setSaving] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const token = localStorage.getItem('token');

  // Set default date to tomorrow + fetch recommendation
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDate(tomorrow.toISOString().split('T')[0]);

    // Fetch AI recommendation
    axios.get(`${API}/booster/schedule/recommendation?card_type=${card.type}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const rec = res.data.recommendation;
      setRecommendation(rec);
      if (rec?.best_time) setTime(rec.best_time);
    }).catch(() => {});
  }, [card.type]);

  const applyRecommendation = () => {
    if (recommendation?.best_time) setTime(recommendation.best_time);
  };

  const handleSave = async () => {
    if (!date || !time) return;
    setSaving(true);
    try {
      await axios.post(`${API}/booster/schedule`, {
        pack_id: packId,
        card_type: card.type,
        platform: 'facebook',
        scheduled_date: date,
        scheduled_time: time,
        message: card.content,
        image_url: card.image_url || null,
        hashtags: card.hashtags || null,
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
      toast.success('Publikacja zaplanowana!');
      onScheduled();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nie udało się zaplanować');
    } finally { setSaving(false); }
  };

  const meta = CARD_META[card.type] || CARD_META.cta;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="schedule-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: meta.bg }}>
              <Clock className="w-4 h-4" style={{ color: meta.color }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ color: 'var(--apisai-text)' }}>Zaplanuj publikację</h3>
              <p className="text-xs" style={{ color: 'var(--apisai-text-muted)' }}>{meta.label} · Facebook</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-lg"><X className="w-4 h-4 text-zinc-400" /></button>
        </div>

        {/* AI Recommendation */}
        {recommendation && (
          <div className="rounded-xl p-3.5 mb-4" style={{ background: '#FFF7ED', border: '1px solid #FDBA7440' }} data-testid="ai-recommendation">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5" style={{ color: '#F97316' }} />
              <span className="text-xs font-semibold" style={{ color: '#EA580C' }}>Rekomendacja</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-sm font-bold" style={{ color: '#9A3412' }}>{recommendation.days?.join(' / ')}</span>
              <span className="text-sm font-bold" style={{ color: '#F97316' }}>{recommendation.time_range}</span>
            </div>
            <p className="text-[11px] leading-relaxed mb-2" style={{ color: '#9A3412CC' }}>{recommendation.tip}</p>
            {recommendation.card_tip && (
              <p className="text-[11px] leading-relaxed" style={{ color: '#9A3412AA' }}>{recommendation.card_tip}</p>
            )}
            <button
              onClick={applyRecommendation}
              className="mt-2.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg"
              style={{ background: '#F9731615', color: '#F97316' }}
              data-testid="apply-recommendation"
            >
              Zastosuj {recommendation.best_time}
            </button>
          </div>
        )}

        <div className="space-y-4 mb-5">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium mb-1.5" style={{ color: 'var(--apisai-text-secondary)' }}>
              <Calendar className="w-3.5 h-3.5" /> Data publikacji
            </label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
              style={{ borderColor: 'var(--apisai-border)' }}
              data-testid="schedule-date"
            />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium mb-1.5" style={{ color: 'var(--apisai-text-secondary)' }}>
              <Clock className="w-3.5 h-3.5" /> Godzina
            </label>
            <input
              type="time"
              value={time}
              onChange={e => setTime(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
              style={{ borderColor: 'var(--apisai-border)' }}
              data-testid="schedule-time"
            />
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: '#EBF5FF' }}>
            <Facebook className="w-4 h-4" style={{ color: '#1877F2' }} />
            <span className="text-xs font-medium" style={{ color: '#1877F2' }}>Platforma: Facebook</span>
          </div>
        </div>

        {/* Preview snippet */}
        <div className="rounded-xl border p-3 mb-5" style={{ borderColor: 'var(--apisai-border)', background: '#FAFAFA' }}>
          <p className="text-xs" style={{ color: 'var(--apisai-text-muted)' }}>Podgląd treści:</p>
          <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--apisai-text)' }}>{card.content.slice(0, 120)}...</p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl h-10 text-xs" data-testid="schedule-cancel">Anuluj</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !date || !time}
            className="flex-1 rounded-xl h-10 text-xs text-white font-medium"
            style={{ background: 'var(--apisai-primary)' }}
            data-testid="schedule-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Clock className="w-3.5 h-3.5 mr-1" /> Zaplanuj</>}
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

const STATUS_MAP = {
  scheduled: { label: 'Zaplanowany', color: '#F59E0B', bg: '#FFFBEB' },
  published: { label: 'Opublikowany', color: '#10B981', bg: '#F0FDF4' },
  failed: { label: 'Błąd', color: '#EF4444', bg: '#FEF2F2' },
  cancelled: { label: 'Anulowany', color: 'var(--zinc-500, #71717a)', bg: '#F3F4F6' },
};

function ScheduledPostsList({ posts, onCancel, onRefresh }) {
  if (!posts.length) return null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border p-5 mb-6" style={{ background: 'white', borderColor: 'var(--apisai-border)' }} data-testid="scheduled-posts-list">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" style={{ color: 'var(--apisai-primary)' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--apisai-text)' }}>Zaplanowane publikacje</h3>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: 'var(--apisai-primary)', color: 'white' }}>{posts.length}</span>
        </div>
        <button onClick={onRefresh} className="text-xs underline" style={{ color: 'var(--apisai-text-muted)' }}>Odśwież</button>
      </div>
      <div className="space-y-2">
        {posts.map((post, idx) => {
          const st = STATUS_MAP[post.status] || STATUS_MAP.scheduled;
          const meta = CARD_META[post.card_type] || CARD_META.cta;
          const dt = new Date(post.scheduled_at);
          const dateStr = dt.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
          const timeStr = dt.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });

          return (
            <div key={post.id || `sched-${idx}`} className="flex items-center gap-3 p-2.5 rounded-xl border" style={{ borderColor: 'var(--apisai-border)' }} data-testid={`scheduled-post-${post.id || idx}`}>
              {/* Thumbnail */}
              <div className="w-10 h-10 rounded-lg flex-shrink-0 overflow-hidden" style={{ background: '#F3F4F6' }}>
                {post.image_url ? (
                  <img src={post.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <meta.icon className="w-4 h-4" style={{ color: meta.color }} />
                  </div>
                )}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-medium" style={{ color: 'var(--apisai-text)' }}>{meta.label}</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                </div>
                <p className="text-[11px] mt-0.5" style={{ color: 'var(--apisai-text-muted)' }}>
                  {dateStr} · {timeStr} · Facebook
                </p>
                {post.error_message && post.status === 'failed' && (
                  <p className="text-[10px] mt-0.5 text-red-500 truncate">{post.error_message}</p>
                )}
              </div>
              {/* Cancel */}
              {post.status === 'scheduled' && (
                <button
                  onClick={() => onCancel(post.id)}
                  className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`cancel-schedule-${post.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-zinc-400 hover:text-red-500" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function FacebookConnectBanner({ onConnect, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border p-4 mb-6 flex items-center gap-4"
      style={{ background: '#EBF5FF', borderColor: '#1877F220' }}
      data-testid="fb-connect-banner"
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#1877F2' }}>
        <Facebook className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold" style={{ color: '#1877F2' }}>Połącz Facebook</p>
        <p className="text-xs" style={{ color: '#1877F2AA' }}>Publikuj posty bezpośrednio na swoją stronę FB</p>
      </div>
      <Button onClick={onConnect} className="rounded-xl h-9 px-4 text-xs font-medium text-white" style={{ background: '#1877F2' }} data-testid="fb-connect-btn">
        <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> Połącz konto
      </Button>
      <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 p-1"><X className="w-4 h-4" /></button>
    </motion.div>
  );
}

function FacebookPageSelector({ pages, onSelect, onCancel, loading }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="fb-page-selector-modal">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#1877F2' }}>
            <Facebook className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold" style={{ color: 'var(--apisai-text)' }}>Wybierz stronę Facebook</h3>
            <p className="text-xs" style={{ color: 'var(--apisai-text-muted)' }}>Na którą stronę chcesz publikować?</p>
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#1877F2' }} /></div>
        ) : pages.length === 0 ? (
          <div className="text-center py-6"><p className="text-sm" style={{ color: 'var(--apisai-text-muted)' }}>Nie znaleziono stron.</p></div>
        ) : (
          <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
            {pages.map(pg => (
              <button key={pg.id} onClick={() => onSelect(pg)} className="w-full text-left p-3 rounded-xl border hover:border-blue-300 hover:bg-blue-50 transition-all" style={{ borderColor: 'var(--apisai-border)' }} data-testid={`fb-page-${pg.id}`}>
                <p className="text-sm font-medium" style={{ color: 'var(--apisai-text)' }}>{pg.name}</p>
                {pg.category && <p className="text-xs" style={{ color: 'var(--apisai-text-muted)' }}>{pg.category}</p>}
              </button>
            ))}
          </div>
        )}
        <Button variant="outline" onClick={onCancel} className="w-full rounded-xl h-9 text-xs" data-testid="fb-page-cancel-btn">Anuluj</Button>
      </motion.div>
    </div>
  );
}

function FacebookPostPreview({ card, businessName, onImageClick }) {
  const initial = (businessName || 'A').charAt(0).toUpperCase();
  return (
    <div className="rounded-2xl border overflow-hidden" style={{ background: 'white', borderColor: '#e4e6eb' }} data-testid={`fb-preview-${card.type}`}>
      {/* FB Header */}
      <div className="flex items-center gap-2.5 px-4 pt-3.5 pb-2">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: '#1877F2' }}>
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold leading-tight" style={{ color: '#050505' }}>{businessName || 'Twoja firma'}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-[11px]" style={{ color: '#65676B' }}>Sponsorowane</span>
            <span className="text-[11px]" style={{ color: '#65676B' }}>·</span>
            <Globe className="w-2.5 h-2.5" style={{ color: '#65676B' }} />
          </div>
        </div>
      </div>

      {/* Post text */}
      <div className="px-4 pb-2.5">
        <p className="text-[14px] leading-[1.35] whitespace-pre-line" style={{ color: '#050505' }}>{card.content}</p>
        {card.hashtags && <p className="text-[13px] mt-1.5" style={{ color: '#1877F2' }}>{card.hashtags}</p>}
      </div>

      {/* Image */}
      {card.image_url && (
        <div className="relative cursor-pointer group" onClick={onImageClick}>
          <img src={card.image_url} alt={card.image_alt || card.title} className="w-full object-cover" style={{ maxHeight: '420px' }} loading="lazy" data-testid={`card-image-${card.type}`} />
          {/* Badge */}
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[10px] font-semibold text-white" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
            Podgląd posta
          </div>
        </div>
      )}

      {/* CTA button if present */}
      {card.extra?.button_text && (
        <div className="px-4 py-2.5 border-t flex items-center justify-between" style={{ borderColor: '#e4e6eb', background: '#F0F2F5' }}>
          <div>
            <p className="text-[12px]" style={{ color: '#65676B' }}>{businessName || 'apisai.pl'}</p>
            {card.extra?.headline && <p className="text-[13px] font-semibold" style={{ color: '#050505' }}>{card.extra.headline}</p>}
          </div>
          <div className="px-3 py-1.5 rounded-md text-[13px] font-semibold" style={{ background: '#e4e6eb', color: '#050505' }}>
            {card.extra.button_text}
          </div>
        </div>
      )}
    </div>
  );
}

function GenericCardPreview({ card, meta }) {
  const Icon = meta.icon;
  return (
    <div className="rounded-2xl border overflow-hidden" style={{ background: 'white', borderColor: 'var(--apisai-border)', boxShadow: 'var(--apisai-shadow)' }} data-testid={`card-preview-${card.type}`}>
      {card.image_url && (
        <div className="relative">
          <img src={card.image_url} alt={card.image_alt || card.title} className="w-full h-48 object-cover" loading="lazy" data-testid={`card-image-${card.type}`} />
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[10px] font-semibold text-white" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
            {meta.label}
          </div>
        </div>
      )}
      <div className="flex items-center gap-3 px-5 py-3" style={{ background: meta.bg }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: meta.color + '20' }}>
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold" style={{ color: meta.color }}>{meta.label}</h3>
          <p className="text-xs" style={{ color: meta.color + 'AA' }}>{meta.desc}</p>
        </div>
      </div>
      <div className="px-5 py-4">
        {card.extra?.headline && <p className="text-base font-bold mb-2" style={{ color: 'var(--apisai-text)' }}>{card.extra.headline}</p>}
        <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--apisai-text)' }}>{card.content}</p>
        {card.hashtags && <p className="text-xs mt-3 font-medium" style={{ color: meta.color }}>{card.hashtags}</p>}
        {card.extra?.button_text && (
          <div className="inline-block mt-3 px-4 py-2 rounded-lg text-sm font-medium" style={{ background: meta.color + '15', color: meta.color }}>{card.extra.button_text}</div>
        )}
        {card.extra?.channel && (
          <div className="flex items-center gap-3 mt-3 text-xs" style={{ color: 'var(--apisai-text-muted)' }}>
            <span>Kanał: {card.extra.channel}</span>
            {card.extra.duration && <span>| {card.extra.duration}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== MAIN COMPONENT ==================== */

export default function BoosterHome({ embedded = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [changingImage, setChangingImage] = useState(null);
  const [businessName, setBusinessName] = useState('');

  // Facebook
  const [fbStatus, setFbStatus] = useState(null);
  const [fbBannerHidden, setFbBannerHidden] = useState(false);
  const [publishingCard, setPublishingCard] = useState(null);
  const [publishedCards, setPublishedCards] = useState({});
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [fbPages, setFbPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);

  // Crop
  const [cropCard, setCropCard] = useState(null);

  // Upload own photo for card
  const [uploadForCard, setUploadForCard] = useState(null);
  const [uploadCropSrc, setUploadCropSrc] = useState(null);
  const cardFileInputRef = useRef(null);

  // Schedule
  const [scheduleCard, setScheduleCard] = useState(null);
  const [scheduledPosts, setScheduledPosts] = useState([]);

  // Ad Graphic
  const [adGraphicCard, setAdGraphicCard] = useState(null);
  const [videoCard, setVideoCard] = useState(null);
  const [showCampaignActive, setShowCampaignActive] = useState(false);
  const [boosterView, setBoosterView] = useState('main'); // main | templates | upload

  // Media Picker
  const [mediaPickerCardType, setMediaPickerCardType] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };
  const profileType = localStorage.getItem('apisai_profile_type') || 'hodowla';

  useEffect(() => {
    fetchCampaignPack();
    fetchFbStatus();
    fetchBusinessName();
    fetchScheduledPosts();
  }, []);

  const fetchCampaignPack = async () => {
    try {
      const res = await axios.get(`${API}/booster/campaign-pack`, { headers: authHeaders });
      setPack(res.data);
    } catch (e) {
      if (e.response?.status === 404) { await generatePack(); return; }
    } finally { setLoading(false); }
  };

  const fetchFbStatus = async () => {
    try {
      const res = await axios.get(`${API}/integrations/facebook/status`, { headers: authHeaders });
      setFbStatus(res.data);
    } catch { setFbStatus({ is_connected: false }); }
  };

  const fetchBusinessName = async () => {
    try {
      const res = await axios.get(`${API}/profile`, { headers: authHeaders });
      setBusinessName(res.data?.business_name || '');
    } catch {}
  };

  const fetchScheduledPosts = async () => {
    try {
      const res = await axios.get(`${API}/booster/schedule`, { headers: authHeaders });
      setScheduledPosts(res.data.posts || []);
    } catch {}
  };

  const cancelScheduledPost = async (postId) => {
    try {
      await axios.delete(`${API}/booster/schedule/${postId}`, { headers: authHeaders });
      toast.success('Publikacja anulowana');
      fetchScheduledPosts();
    } catch { toast.error('Nie udało się anulować'); }
  };

  const generatePack = async (templateType = null, customImageUrls = null) => {
    setGenerating(true);
    try {
      const payload = { regenerate: true };
      if (templateType) payload.template_type = templateType;
      if (customImageUrls) payload.custom_image_urls = customImageUrls;
      const res = await axios.post(`${API}/booster/campaign-pack`, payload, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setPack(res.data);
      setPublishedCards({});
      toast.success('Kampania gotowa!');
    } catch (e) {
      if (e.response?.status === 400) {
        toast.error('Najpierw uzupełnij profil', { action: { label: 'Uzupełnij', onClick: () => navigate('/panel/profil') } });
      } else { toast.error('Nie udało się przygotować kampanii'); }
    } finally { setGenerating(false); setLoading(false); }
  };

  const copyToClipboard = async (card) => {
    let text = card.content;
    if (card.hashtags) text += '\n\n' + card.hashtags;
    if (card.extra?.button_text) text += '\n\nPrzycisk: ' + card.extra.button_text;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(card.type);
      toast.success('Skopiowano do schowka');
      setTimeout(() => setCopiedId(null), 2000);
    } catch { toast.error('Nie udało się skopiować'); }
  };

  const downloadImage = async (card) => {
    if (!card.image_url) return;
    try {
      const resp = await fetch(card.image_url);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `apisai-${card.type}.jpg`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Zdjęcie pobrane!');
    } catch { window.open(card.image_url, '_blank'); }
  };

  const changeImage = async (cardType) => {
    setChangingImage(cardType);
    try {
      const res = await axios.post(`${API}/booster/campaign-pack/change-image`, { card_type: cardType, pack_id: pack?.id }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setPack(prev => ({ ...prev, cards: prev.cards.map(c => c.type === cardType ? { ...c, image_url: res.data.image_url, image_alt: res.data.image_alt } : c) }));
      toast.success('Zdjęcie zmienione!');
    } catch { toast.error('Nie udało się zmienić zdjęcia'); }
    finally { setChangingImage(null); }
  };

  const handleCropSave = (croppedUrl) => {
    if (!cropCard) return;
    setPack(prev => ({ ...prev, cards: prev.cards.map(c => c.type === cropCard.type ? { ...c, image_url: croppedUrl } : c) }));
    toast.success('Zdjęcie skadrowane!');
    setCropCard(null);
  };

  // Upload own photo for a specific campaign card
  const triggerUploadForCard = (cardType) => {
    setUploadForCard(cardType);
    setTimeout(() => cardFileInputRef.current?.click(), 50);
  };

  const handleCardFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setUploadForCard(null); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Wybierz zdjęcie JPG, PNG lub WebP');
      setUploadForCard(null); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Zdjęcie max 10MB');
      setUploadForCard(null); return;
    }
    const previewUrl = URL.createObjectURL(file);
    setUploadCropSrc(previewUrl);
    e.target.value = '';
  };

  const handleUploadCropSave = async (croppedBlobUrl) => {
    if (!uploadForCard) return;
    setUploadCropSrc(null);
    try {
      const resp = await fetch(croppedBlobUrl);
      const blob = await resp.blob();
      const formData = new FormData();
      formData.append('file', new File([blob], `own-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      const res = await axios.post(`${API}/booster/upload-photo`, formData, { headers: authHeaders });
      setPack(prev => ({ ...prev, cards: prev.cards.map(c => c.type === uploadForCard ? { ...c, image_url: res.data.url } : c) }));
      toast.success('Twoje zdjęcie dodane!');
    } catch {
      toast.error('Nie udało się wgrać zdjęcia');
    } finally {
      setUploadForCard(null);
    }
  };

  // Campaign management
  const [pausing, setPausing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Handle selecting image from media library
  const handleMediaSelect = (mediaItem) => {
    if (!mediaPickerCardType) return;
    const url = mediaItem.file_url || mediaItem.thumbnail_url;
    setPack(prev => ({
      ...prev,
      cards: prev.cards.map(c =>
        c.type === mediaPickerCardType ? { ...c, image_url: url } : c
      )
    }));
    setMediaPickerCardType(null);
    toast.success('Zdjęcie z biblioteki dodane!');
  };

  const pauseCampaign = async () => {
    setPausing(true);
    try {
      await axios.post(`${API}/booster/pause`, {}, { headers: authHeaders });
      toast.success('Kampania wstrzymana');
      fetchCampaignPack();
    } catch {
      toast.error('Nie udało się wstrzymać kampanii');
    }
    setPausing(false);
  };

  const deleteCampaign = async () => {
    if (!pack?.id) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/booster/campaign-library/${pack.id}`, { headers: authHeaders });
      toast.success('Kampania usunięta');
      setPack(null);
      setShowDeleteConfirm(false);
    } catch {
      toast.error('Nie udało się usunąć kampanii');
    }
    setDeleting(false);
  };


  // Facebook
  const connectFacebook = async () => {
    try {
      const res = await axios.get(`${API}/integrations/facebook/connect-url`, { headers: authHeaders });
      window.location.href = res.data.oauth_url;
    } catch { toast.error('Nie udało się połączyć z Facebookiem'); }
  };

  const handleSelectPage = async (page) => {
    try {
      await axios.post(`${API}/integrations/facebook/pages/select`, { page_id: page.id, page_name: page.name }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setFbStatus(prev => ({ ...prev, selected_page: { id: page.id, name: page.name } }));
      setShowPageSelector(false);
      toast.success(`Strona "${page.name}" wybrana!`);
    } catch { toast.error('Nie udało się wybrać strony'); }
  };

  const openPageSelector = async () => {
    setShowPageSelector(true);
    setLoadingPages(true);
    try {
      const res = await axios.get(`${API}/integrations/facebook/pages`, { headers: authHeaders });
      setFbPages(res.data.pages || []);
    } catch (e) {
      if (e.response?.data?.detail?.error_code === 'FB_TOKEN_EXPIRED') {
        toast.error('Token wygasł. Połącz ponownie z Facebookiem.');
        setShowPageSelector(false);
      } else { toast.error('Nie udało się pobrać stron'); }
    } finally { setLoadingPages(false); }
  };

  const publishToFacebook = async (card) => {
    if (!fbStatus?.is_connected) { connectFacebook(); return; }
    if (!fbStatus?.selected_page) { await openPageSelector(); return; }

    setPublishingCard(card.type);
    try {
      const res = await axios.post(`${API}/integrations/facebook/publish`, {
        card_type: card.type, text: card.content, image_url: card.image_url || null, hashtags: card.hashtags || null,
      }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setPublishedCards(prev => ({ ...prev, [card.type]: true }));
      toast.success(res.data.message || 'Opublikowano na Facebooku!');
      setShowCampaignActive(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = detail?.error_code;
      if (code === 'NO_PAGE_SELECTED') { await openPageSelector(); }
      else if (code === 'FB_TOKEN_EXPIRED') { toast.error('Token wygasł.', { action: { label: 'Połącz', onClick: connectFacebook } }); }
      else { toast.error(detail?.message || 'Nie udało się opublikować'); }
    } finally { setPublishingCard(null); }
  };

  /* ==================== RENDER ==================== */

  if (loading) {
    const loadingContent = <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--apisai-primary)' }} /></div>;
    return embedded ? loadingContent : <PanelLayout>{loadingContent}</PanelLayout>;
  }

  const fbConnected = fbStatus?.is_connected;
  const fbPage = fbStatus?.selected_page;

  const content = (
    <div className="max-w-[1200px] mx-auto" data-testid="booster-campaign-pack">
      {/* Header — only standalone */}
      {!embedded && (
        <motion.div className="text-center mb-6" initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
            <Rocket className="w-3.5 h-3.5 text-[#F47A3B]" />
            <span className="text-[12px] font-semibold text-[#F47A3B]">Gotowe kampanie do publikacji</span>
          </div>
          <h1 className="text-3xl font-extrabold text-zinc-900 mb-2">{pack?.cards ? 'Twoja kampania' : 'Kampanie'}</h1>
          <p className="text-[15px] text-zinc-500 max-w-md mx-auto">{pack?.cards ? 'Zarządzaj postami i publikuj' : 'Stwórz nową kampanię w kilka kliknięć'}</p>
          <div className="flex justify-center gap-3 mt-4">
            <div className="flex items-center gap-2">
              {pack?.cards && (
                <>
                  <Button
                    variant="outline"
                    onClick={pauseCampaign}
                    disabled={pausing}
                    className="rounded-xl h-9 px-3 text-xs font-medium border-[#F59E0B] text-[#F59E0B] hover:bg-[#FFFBEB]"
                    data-testid="pause-campaign-btn"
                  >
                    {pausing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Clock className="w-3.5 h-3.5 mr-1" />}
                    Zatrzymaj
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="rounded-xl h-9 px-3 text-xs font-medium border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2]"
                    data-testid="delete-campaign-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Usuń
                  </Button>
                </>
              )}
              <Button onClick={() => navigate('/panel/publikacja')} className="rounded-xl h-9 px-4 text-xs font-medium text-white bg-[#F47A3B] hover:bg-[#E06A2B]" data-testid="create-campaign-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Nowa promocja
              </Button>
            </div>
          </div>
        </motion.div>
        )}

        {/* Empty state — no campaign */}
        {!pack?.cards && !generating && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border p-8 text-center bg-white border-zinc-200" data-testid="no-pack-state">
            <Sparkles className="w-10 h-10 mx-auto mb-4 text-[#F47A3B]" />
            <h3 className="text-lg font-semibold mb-2 text-zinc-900">Nie masz aktywnej kampanii</h3>
            <p className="text-sm mb-6 text-zinc-500">Stwórz nową kampanię — przygotujemy gotowe posty i reklamy.</p>
            <Button onClick={() => navigate('/panel/grupy')} className="rounded-xl h-10 px-6 text-white font-medium bg-[#F47A3B] hover:bg-[#E06A2B]" data-testid="create-campaign-empty">
              <Rocket className="w-4 h-4 mr-2" /> Zacznij promocję
            </Button>
          </motion.div>
        )}

        {/* Generating */}
        {generating && (
          <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center">
            <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin text-[#F47A3B]" />
            <h3 className="text-lg font-semibold mb-1 text-zinc-900">Przygotowujemy kampanię...</h3>
            <p className="text-sm text-zinc-500">Dobieramy treści i zdjęcia dla Twojej firmy</p>
          </div>
        )}

        {/* Campaign content — 2 columns */}
        {pack?.cards && !generating && (
        <div className="flex gap-6">
          {/* LEFT — Cards */}
          <div className="flex-1 min-w-0">

        {/* TOP BOX — action prompt */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-5 mb-4 flex items-center gap-4" data-testid="publish-top-box">
          <div className="w-10 h-10 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
            <Send className="w-5 h-5 text-[#F47A3B]" />
          </div>
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-zinc-900">Masz gotowe posty — opublikuj je i zdobądź zapytania</p>
          </div>
          <Button
            onClick={() => {
              const firstCard = pack?.cards?.[0];
              if (firstCard) publishToFacebook(firstCard);
            }}
            className="rounded-xl h-9 px-5 text-[13px] font-bold text-white flex-shrink-0"
            style={{ background: '#1877F2' }}
            data-testid="publish-top-cta"
          >
            <Send className="w-4 h-4 mr-1.5" /> Opublikuj teraz
          </Button>
        </div>

        {/* FB Connected status */}
        {fbConnected && fbPage && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border px-4 py-2.5 mb-4 flex items-center gap-3" style={{ background: '#F0FDF4', borderColor: '#10B98130' }} data-testid="fb-connected-status">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
            <p className="text-xs flex-1" style={{ color: '#059669' }}>Facebook połączony — publikujesz na <strong>{fbPage.name}</strong></p>
            <button onClick={openPageSelector} className="text-xs font-medium underline" style={{ color: '#059669' }} data-testid="fb-change-page-btn">Zmień stronę</button>
          </motion.div>
        )}

        {/* FB Connected, no page */}
        {fbConnected && !fbPage && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border px-4 py-2.5 mb-4 flex items-center gap-3" style={{ background: '#FFFBEB', borderColor: '#F59E0B30' }} data-testid="fb-no-page-status">
            <Facebook className="w-4 h-4 flex-shrink-0" style={{ color: '#F59E0B' }} />
            <p className="text-xs flex-1" style={{ color: '#D97706' }}>Facebook połączony, ale nie wybrano strony.</p>
            <button onClick={openPageSelector} className="text-xs font-medium underline" style={{ color: '#D97706' }} data-testid="fb-select-page-btn">Wybierz stronę</button>
          </motion.div>
        )}

        {/* Campaign Cards */}
            <div className="space-y-6 mb-8">
              {(pack?.cards || []).map((card, i) => {
                const meta = CARD_META[card.type] || CARD_META.cta;
                const isCopied = copiedId === card.type;
                const isChanging = changingImage === card.type;
                const isPublishing = publishingCard === card.type;
                const isPublished = publishedCards[card.type];
                const isFbCard = meta.platform === 'facebook';

                return (
                  <motion.div
                    key={card.type}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08 }}
                    data-testid={`campaign-card-${card.type}`}
                  >
                    {/* Card preview: FB style for facebook/instagram, generic for others */}
                    {(isFbCard || meta.platform === 'instagram') ? (
                      <FacebookPostPreview
                        card={card}
                        businessName={businessName || fbPage?.name}
                        onImageClick={() => card.image_url && setCropCard(card)}
                      />
                    ) : (
                      <GenericCardPreview card={card} meta={meta} />
                    )}

                    {/* Action bar — simplified */}
                    <div className="flex items-center gap-2 mt-3" data-testid={`actions-${card.type}`}>
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(card)}
                        className="rounded-xl h-8 text-xs px-3 border"
                        style={{ borderColor: isCopied ? '#10B981' : 'var(--apisai-border)', color: isCopied ? '#10B981' : 'var(--apisai-text-secondary)', background: isCopied ? '#F0FDF4' : 'white' }}
                        data-testid={`copy-${card.type}`}
                      >
                        {isCopied ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Skopiowano</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Kopiuj</>}
                      </Button>

                      {card.image_url && (
                        <Button
                          variant="outline"
                          onClick={() => setMediaPickerCardType(card.type)}
                          className="rounded-xl h-8 text-xs px-3 border bg-white"
                          style={{ borderColor: 'var(--apisai-border)', color: 'var(--apisai-text-secondary)' }}
                          data-testid={`library-btn-${card.type}`}
                        >
                          <ImageIcon className="w-3.5 h-3.5 mr-1" /> Zmień zdjęcie
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        onClick={() => setScheduleCard(card)}
                        className="rounded-xl h-8 text-xs px-3 border bg-white"
                        style={{ borderColor: 'var(--apisai-border)', color: 'var(--apisai-text-secondary)' }}
                        data-testid={`schedule-btn-${card.type}`}
                      >
                        <Clock className="w-3.5 h-3.5 mr-1" /> Zaplanuj
                      </Button>

                      <Button
                        onClick={() => publishToFacebook(card)}
                        disabled={isPublishing || isPublished}
                        className="rounded-xl h-8 text-xs px-3 font-medium text-white ml-auto"
                        style={{ background: isPublished ? '#10B981' : '#1877F2', opacity: isPublishing ? 0.7 : 1 }}
                        data-testid={`publish-fb-${card.type}`}
                      >
                        {isPublishing ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Publikuję...</> : isPublished ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Opublikowano</> : <><Send className="w-3.5 h-3.5 mr-1" /> Publikuj</>}
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Scheduled Posts List */}
            <ScheduledPostsList
              posts={scheduledPosts}
              onCancel={cancelScheduledPost}
              onRefresh={fetchScheduledPosts}
            />

            {/* Bottom regenerate */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-center">
              <Button onClick={() => navigate('/panel/grupy')} variant="outline" className="rounded-xl h-10 px-6 border-2" style={{ borderColor: 'var(--apisai-primary)', color: 'var(--apisai-primary)' }} data-testid="regenerate-pack-btn">
                <Plus className="w-4 h-4 mr-2" /> Nowa promocja
              </Button>
            </motion.div>

        {/* Modals */}
        {showCampaignActive && (
          <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" data-testid="campaign-active-modal">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: 'spring', damping: 20 }}
              className="bg-white rounded-2xl max-w-md w-full p-8 text-center shadow-2xl">
              <div className="w-16 h-16 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto mb-5">
                <Rocket className="w-8 h-8 text-[#10B981]" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900 mb-2">Kampania aktywna</h2>
              <p className="text-[15px] text-zinc-500 mb-6">Teraz zbieramy klientów dla Ciebie</p>
              <div className="flex flex-col gap-3">
                <Button onClick={() => { setShowCampaignActive(false); navigate('/panel/leady'); }}
                  className="w-full rounded-xl h-11 text-white font-semibold bg-[#F47A3B] hover:bg-[#E06A2B]"
                  data-testid="campaign-active-check-leads">
                  <Inbox className="w-4 h-4 mr-2" /> Sprawdź zapytania
                </Button>
                <button onClick={() => setShowCampaignActive(false)}
                  className="text-sm text-zinc-400 hover:text-zinc-500 transition-colors"
                  data-testid="campaign-active-close">
                  Wróć do kampanii
                </button>
              </div>
            </motion.div>
          </div>
        )}
        {showPageSelector && <FacebookPageSelector pages={fbPages} onSelect={handleSelectPage} onCancel={() => setShowPageSelector(false)} loading={loadingPages} />}
        {cropCard && cropCard.image_url && <ImageCropModal imageUrl={cropCard.image_url} onSave={handleCropSave} onClose={() => setCropCard(null)} />}
        {uploadCropSrc && <ImageCropModal imageUrl={uploadCropSrc} onSave={handleUploadCropSave} onClose={() => { setUploadCropSrc(null); setUploadForCard(null); }} />}
        {scheduleCard && <ScheduleModal card={scheduleCard} packId={pack?.id} onClose={() => setScheduleCard(null)} onScheduled={fetchScheduledPosts} />}
        {adGraphicCard && adGraphicCard.image_url && <AdGraphicEditor imageUrl={adGraphicCard.image_url} cardType={adGraphicCard.type} onClose={() => setAdGraphicCard(null)} />}
        {videoCard && videoCard.image_url && <VideoEditor imageUrl={videoCard.image_url} cardType={videoCard.type} cardContent={videoCard} onClose={() => setVideoCard(null)} />}
        {mediaPickerCardType && <MediaPicker open={true} onSelect={handleMediaSelect} onClose={() => setMediaPickerCardType(null)} />}
        {/* Hidden file input for uploading own photo to card */}
        <input
          ref={cardFileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleCardFileSelect}
          className="hidden"
          data-testid="card-file-input"
        />

          </div>

          {/* RIGHT — Guiding Panel */}
          <div className="w-[300px] flex-shrink-0 hidden lg:block" data-testid="booster-right-column">
            <div className="sticky top-[80px] space-y-4">
              <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="context-panel">
                <h3 className="text-[15px] font-bold text-zinc-900 mb-2">Co Ci to daje</h3>
                <p className="text-sm text-zinc-500 mb-4">Każdy post to szansa na nowego klienta. Skopiuj i opublikuj na Facebooku.</p>
                <div className="space-y-2 mb-4">
                  {[
                    `${pack?.cards?.length || 0} postów gotowych`,
                    fbConnected ? 'Facebook połączony' : 'Połącz Facebook, aby publikować',
                    Object.keys(publishedCards).length > 0 ? 'Posty opublikowane' : 'Gotowe do publikacji',
                  ].map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-[#F47A3B]" />
                      </div>
                      <span className="text-sm text-zinc-600">{b}</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-[#F47A3B]/5 border border-[#F47A3B]/10 rounded-xl">
                  <p className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider mb-1">Co teraz</p>
                  <p className="text-sm text-[#F47A3B] font-medium">Skopiuj tekst posta i opublikuj na Facebooku.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-sm mx-4 shadow-xl"
            data-testid="delete-confirm-modal"
          >
            <div className="w-12 h-12 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-[#EF4444]" />
            </div>
            <h3 className="text-[16px] font-bold text-zinc-900 text-center mb-2">Usunąć kampanię?</h3>
            <p className="text-[13px] text-zinc-500 text-center mb-5">
              Wszystkie posty i materiały zostaną trwale usunięte. Tej operacji nie można cofnąć.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 rounded-xl h-10 text-[13px] font-medium"
                data-testid="delete-cancel-btn"
              >
                Anuluj
              </Button>
              <Button
                onClick={deleteCampaign}
                disabled={deleting}
                className="flex-1 rounded-xl h-10 text-[13px] font-medium text-white bg-[#EF4444] hover:bg-[#DC2626]"
                data-testid="delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Usuń kampanię'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );

  return embedded ? content : <PanelLayout>{content}</PanelLayout>;
}